// DataSourceBindingDrawer — "Internal data source": the drawer behind a
// dataSource field's "Manage data source" button. Two collapsible sections —
// Internal Data Source Details (pick a source from GET /datasource/selection,
// then map its Display and Value columns) and Bind Field to Data Source (a
// FIELD ID ⇄ DATA SOURCE COLUMN table, so a lookup can fill other fields).
//
// Everything is edited as a LOCAL DRAFT seeded from the field's properties on
// open: Done commits one patch (dataSource / displayColumn / valueColumn /
// bindingList) and Close discards. No API write — bindings live in the
// element's properties and persist with the normal elements PATCH on save.
//
// Switching the data source clears the column mapping and the bindings: those
// ids belong to the source that was just replaced.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, Icon, IconButton, SelectDropdown } from '@/components/common';
import { useGetDataSourceSelectionQuery } from '@/api/endpoints/datasourceApi';
import type { BindableField } from './utils/builderModel';
import type { DataSourceBinding } from './utils/formElementDefaults';

interface DataSourceBindingDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Current values from the field's basicProperties. */
  dataSource: string;
  displayColumn: string;
  valueColumn: string;
  bindingList: DataSourceBinding[];
  bindableFields: BindableField[];
  /** Commit — called once, on Done. */
  onApply: (next: {
    dataSource: string;
    displayColumn: string;
    valueColumn: string;
    bindingList: DataSourceBinding[];
  }) => void;
}

const DataSourceBindingDrawer = ({
  open,
  onClose,
  dataSource,
  displayColumn,
  valueColumn,
  bindingList,
  bindableFields,
  onApply,
}: DataSourceBindingDrawerProps) => {
  const { t } = useTranslation();
  const { data: sources = [] } = useGetDataSourceSelectionQuery(undefined, { skip: !open });

  // Section accordions (both open in the design). No shared accordion
  // component exists — the properties-panel sections do the same locally.
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [bindOpen, setBindOpen] = useState(true);

  // Draft state — re-seeded from props on every open, so Close really discards.
  const [source, setSource] = useState(dataSource);
  const [display, setDisplay] = useState(displayColumn);
  const [value, setValue] = useState(valueColumn);
  const [bindings, setBindings] = useState<DataSourceBinding[]>(bindingList);
  const [draftField, setDraftField] = useState('');
  const [draftColumn, setDraftColumn] = useState('');

  useEffect(() => {
    if (!open) return;
    setSource(dataSource);
    setDisplay(displayColumn);
    setValue(valueColumn);
    setBindings(bindingList);
    setDraftField('');
    setDraftColumn('');
  }, [open, dataSource, displayColumn, valueColumn, bindingList]);

  const columns = sources.find((s) => s.id === source)?.columns ?? [];
  const columnName = (id: string) => columns.find((c) => c.columnId === id)?.columnName ?? id;
  const fieldTitle = (id: string) => bindableFields.find((f) => f.id === id)?.title ?? id;

  // A field can only be filled from one column, so bound ones drop out.
  const boundIds = new Set(bindings.map((b) => b.fieldId));
  const availableFields = bindableFields.filter((f) => !boundIds.has(f.id));

  const pickSource = (next: string) => {
    setSource(next);
    // The old source's column ids mean nothing under the new one.
    setDisplay('');
    setValue('');
    setBindings([]);
    setDraftField('');
    setDraftColumn('');
  };

  const addBinding = () => {
    if (!draftField || !draftColumn) return;
    setBindings((prev) => [...prev, { fieldId: draftField, columnId: draftColumn }]);
    setDraftField('');
    setDraftColumn('');
  };

  const done = () => {
    onApply({
      dataSource: source,
      displayColumn: display,
      valueColumn: value,
      bindingList: bindings,
    });
    onClose();
  };

  const columnOptions = columns.map((c) => ({ value: c.columnId, label: c.columnName }));
  const placeholderOption = { value: '', label: t('forms_builder_ds_select') };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      icon='database'
      title={t('forms_builder_ds_drawer_title')}
      subtitle={t('forms_builder_ds_drawer_subtitle')}
      className='ds-binding'
      footer={
        <>
          <Button onClick={done}>{t('forms_builder_ds_done')}</Button>
          <Button variant='secondary' onClick={onClose}>
            {t('forms_builder_ds_close')}
          </Button>
        </>
      }
    >
      {/* ---- Internal Data Source Details ---- */}
      <section className='ds-binding__card'>
        <button
          type='button'
          className='ds-binding__head'
          aria-expanded={detailsOpen}
          onClick={() => setDetailsOpen((o) => !o)}
        >
          <span className='ds-binding__head-title'>{t('forms_builder_ds_details_title')}</span>
          <Icon name={detailsOpen ? 'chevron-up' : 'chevron-down'} size={18} />
        </button>
        {detailsOpen && (
          <div className='ds-binding__body'>
            <label className='ds-binding__field'>
              <span className='ds-binding__label'>{t('forms_builder_ds_source')}</span>
              <SelectDropdown
                value={source}
                options={[
                  placeholderOption,
                  ...sources.map((s) => ({
                    value: s.id,
                    label: s.schemaName,
                  })),
                ]}
                onChange={(next) => pickSource(next)}
                portal
              />
            </label>
            <div className='ds-binding__pair'>
              <label className='ds-binding__field'>
                <span className='ds-binding__label'>{t('forms_builder_ds_display_column')}</span>
                <SelectDropdown
                  value={display}
                  disabled={!source}
                  options={[placeholderOption, ...columnOptions]}
                  onChange={(next) => setDisplay(next)}
                  portal
                />
              </label>
              <label className='ds-binding__field'>
                <span className='ds-binding__label'>{t('forms_builder_ds_value_column')}</span>
                <SelectDropdown
                  value={value}
                  disabled={!source}
                  options={[placeholderOption, ...columnOptions]}
                  onChange={(next) => setValue(next)}
                  portal
                />
              </label>
            </div>
          </div>
        )}
      </section>

      {/* ---- Bind Field to Data Source ---- */}
      <section className='ds-binding__card'>
        <button
          type='button'
          className='ds-binding__head'
          aria-expanded={bindOpen}
          onClick={() => setBindOpen((o) => !o)}
        >
          <span className='ds-binding__head-title'>{t('forms_builder_ds_bind_title')}</span>
          <Icon name={bindOpen ? 'chevron-up' : 'chevron-down'} size={18} />
        </button>
        {bindOpen && (
          <div className='ds-binding__body'>
            <div className='ds-binding__row ds-binding__row--head'>
              <span>{t('forms_builder_ds_col_field')}</span>
              <span>{t('forms_builder_ds_col_column')}</span>
              <span />
            </div>
            {bindings.map((b) => (
              <div key={`${b.fieldId}-${b.columnId}`} className='ds-binding__row'>
                <span className='ds-binding__cell'>{fieldTitle(b.fieldId)}</span>
                <span className='ds-binding__cell ds-binding__cell--muted'>
                  {columnName(b.columnId)}
                </span>
                <IconButton
                  icon='trash-2'
                  variant='ghost'
                  size='sm'
                  className='ds-binding__remove'
                  aria-label={t('forms_builder_ds_remove_binding')}
                  onClick={() =>
                    setBindings((prev) =>
                      prev.filter((x) => !(x.fieldId === b.fieldId && x.columnId === b.columnId)),
                    )
                  }
                />
              </div>
            ))}

            <div className='ds-binding__pair'>
              <SelectDropdown
                aria-label={t('forms_builder_ds_col_field')}
                value={draftField}
                disabled={!source}
                options={[
                  placeholderOption,
                  ...availableFields.map((f) => ({ value: f.id, label: f.title })),
                ]}
                onChange={(next) => setDraftField(next)}
                portal
              />
              <SelectDropdown
                aria-label={t('forms_builder_ds_col_column')}
                value={draftColumn}
                disabled={!source}
                options={[placeholderOption, ...columnOptions]}
                onChange={(next) => setDraftColumn(next)}
                portal
              />
            </div>
            <Button
              iconStart='plus'
              className='ds-binding__add'
              disabled={!draftField || !draftColumn}
              onClick={addBinding}
            >
              {t('forms_builder_ds_add_binding')}
            </Button>
          </div>
        )}
      </section>
    </Drawer>
  );
};

export default DataSourceBindingDrawer;
